import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, CircleHelp, LogOut, Menu, Search, User as UserIcon, X } from "lucide-react";
import { apiFetch, clearToken, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ICONLESS = "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm";

export const AppShell = ({ children }) => {
  const [shell, setShell] = useState(null);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [help, setHelp] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  const load = useCallback(() => {
    if (!getToken()) return;
    apiFetch("/portal/home").then((r) => (r.ok ? r.json() : null)).then(setShell);
  }, []);

  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => { setOpen(false); setMenu(false); setHelp(false); setResults(null); },
            [location.pathname]);

  const search = async (event) => {
    event?.preventDefault();
    if (!query.trim()) return;
    const r = await apiFetch(`/search?q=${encodeURIComponent(query.trim())}`);
    setResults(r.ok ? await r.json() : { results: [], error: true });
  };

  const signOut = () => { clearToken(); navigate("/login"); };

  if (!getToken()) return <>{children}</>;

  return (
    <div className="min-h-screen bg-slate-50 lg:flex" data-testid="app-shell">
      <a href="#shell-main" className="sr-only focus:not-sr-only focus:absolute focus:z-50
                                       focus:m-2 focus:rounded focus:bg-white focus:p-2"
         data-testid="shell-skip-link">Skip to main content</a>

      {/* ---------------------------------------------------------------- left navigation */}
      <aside className={`${open ? "block" : "hidden"} lg:block lg:w-64 lg:shrink-0
                         bg-[var(--hcd-blue)] text-white lg:min-h-screen`}
             data-testid="shell-sidebar">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/15">
          <span className="grid h-10 w-10 place-items-center rounded-md bg-white/95
                           text-[10px] font-bold text-[var(--hcd-blue)]" aria-hidden="true">HCD</span>
          <span>
            <span className="block text-[10px] uppercase tracking-widest text-white/85">
              Health Care District
            </span>
            <span className="block text-sm font-semibold leading-tight">
              Enterprise Service Center
            </span>
          </span>
        </div>

        <nav aria-label="Primary" className="px-3 py-4 space-y-5">
          <Link to="/" data-testid="nav-home"
                className={`${ICONLESS} font-medium ${location.pathname === "/"
                  ? "bg-white/15" : "hover:bg-white/10"}`}>
            Home
          </Link>
          {(shell?.navigation || []).map((group) => (
            <div key={group.title}>
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest
                            text-white/80">
                {group.title}
              </p>
              <ul>
                {group.links.map((link) => (
                  <li key={link.label}>
                    {link.disabled ? (
                      <span className={`${ICONLESS} text-white/85 cursor-not-allowed`}
                            title={link.why}
                            data-testid={`nav-disabled-${link.label.toLowerCase()
                              .replace(/[^a-z]+/g, "-")}`}>
                        {link.label}
                        <span className="text-[10px] uppercase">soon</span>
                      </span>
                    ) : (
                      <Link to={link.path}
                            data-testid={`nav-${link.label.toLowerCase()
                              .replace(/[^a-z]+/g, "-")}`}
                            className={`${ICONLESS} ${location.pathname === link.path
                              ? "bg-white/15 font-medium" : "text-white/85 hover:bg-white/10"}`}>
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 min-w-0">
        {/* --------------------------------------------------------------------- top bar */}
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b
                           border-slate-200 bg-white px-4 py-3 lg:px-6"
                data-testid="shell-topbar">
          <Button variant="ghost" size="sm" className="lg:hidden" data-testid="shell-nav-toggle"
                  aria-label={open ? "Close navigation" : "Open navigation"}
                  onClick={() => setOpen(!open)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <form onSubmit={search} className="order-3 w-full lg:order-none lg:max-w-xl lg:flex-1"
                role="search">
            <label htmlFor="shell-search" className="sr-only">
              Search services, articles, tickets and people
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4
                                 text-slate-400" aria-hidden="true" />
              <Input id="shell-search" ref={searchRef} data-testid="shell-search-input"
                     className="pl-9" value={query} placeholder="Search services, articles,
                     tickets, people" onChange={(e) => setQuery(e.target.value)} />
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1">
            <Button asChild variant="ghost" size="sm" data-testid="shell-notifications"
                    aria-label={`Notifications: ${shell?.unread_notifications || 0} unread`}>
              <Link to="/me" className="relative">
                <Bell className="h-5 w-5" aria-hidden="true" />
                {shell?.unread_notifications > 0 && (
                  <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center
                                   rounded-full bg-red-700 px-1 text-[10px] font-semibold
                                   text-white" data-testid="shell-unread-badge">
                    {shell.unread_notifications > 99 ? "99+" : shell.unread_notifications}
                  </span>
                )}
              </Link>
            </Button>
            <Button variant="ghost" size="sm" data-testid="shell-help"
                    aria-expanded={help} aria-label="Help"
                    onClick={() => setHelp(!help)}>
              <CircleHelp className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button variant="ghost" size="sm" className="gap-2" data-testid="shell-user-menu"
                    aria-expanded={menu} onClick={() => setMenu(!menu)}>
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--hcd-blue)]
                               text-xs font-semibold text-white" aria-hidden="true">
                {(shell?.person?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-medium leading-tight">
                  {shell?.person?.name || "Signed in"}
                </span>
                <span className="block text-xs text-slate-600 leading-tight">
                  {(shell?.person?.roles || []).slice(0, 2).join(" · ").replace(/_/g, " ")
                   || "employee"}
                </span>
              </span>
            </Button>
          </div>

          {help && (
            <div className="order-4 w-full rounded-md border border-slate-200 bg-slate-50 p-3
                            text-sm" data-testid="shell-help-panel">
              Search finds services, knowledge articles, tickets and people you are allowed to
              see. Report an issue or request something from the catalogue; anything waiting on
              your decision sits under My Approvals. This is a non-production environment with
              synthetic data only.
            </div>
          )}

          {menu && (
            <div className="order-5 w-full rounded-md border border-slate-200 bg-white p-4
                            text-sm space-y-2 lg:w-80 lg:ml-auto" data-testid="shell-user-panel">
              <p className="font-medium" data-testid="shell-user-name">
                {shell?.person?.name}
              </p>
              <p className="text-slate-600" data-testid="shell-user-email">
                {shell?.person?.email || "no work email recorded"}
              </p>
              <p className="text-xs text-slate-600">
                Roles: {(shell?.person?.roles || []).join(", ").replace(/_/g, " ") || "employee"}
              </p>
              {shell?.person?.provider_memberships?.length > 0 ? (
                <p className="text-xs text-slate-600" data-testid="shell-user-teams">
                  Teams: {shell.person.provider_memberships.join(", ")}
                </p>
              ) : (
                <p className="text-xs text-slate-600" data-testid="shell-user-teams">
                  Teams: you are not a member of a provider team
                </p>
              )}
              {shell?.person?.admin_provider_scopes?.length > 0 && (
                <p className="text-xs text-slate-600">
                  Administers: {shell.person.admin_provider_scopes.join(", ")}
                </p>
              )}
              <div className="flex gap-2 pt-1">
                <Button asChild size="sm" variant="outline" data-testid="shell-profile-link">
                  <Link to="/me"><UserIcon className="mr-1 h-4 w-4" />My profile</Link>
                </Button>
                <Button size="sm" variant="outline" data-testid="shell-signout"
                        onClick={signOut}>
                  <LogOut className="mr-1 h-4 w-4" />Sign out
                </Button>
              </div>
            </div>
          )}
        </header>

        {results && (
          <div className="border-b border-slate-200 bg-white px-4 py-3 lg:px-6"
               data-testid="shell-search-results">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {results.results?.length || 0} result(s) for “{query}”
              </p>
              <Button size="sm" variant="ghost" data-testid="shell-search-clear"
                      onClick={() => setResults(null)}>Clear</Button>
            </div>
            <ul className="mt-2 space-y-1 text-sm">
              {(results.results || []).slice(0, 8).map((row) => (
                <li key={`${row.type}-${row.id}`} className="text-slate-700"
                    data-testid={`shell-search-row-${row.type}`}>
                  <span className="text-xs uppercase text-slate-600">{row.type}</span>{" "}
                  {row.title || row.label || row.id}
                </li>
              ))}
              {(results.results || []).length === 0 && (
                <li className="text-slate-600" data-testid="shell-search-empty">
                  Nothing you are allowed to see matches that.
                </li>
              )}
            </ul>
          </div>
        )}

        <main id="shell-main" className="min-w-0">{children}</main>
      </div>
    </div>
  );
};
